import React from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import './CalendarPage.css';

type CalendarView = 'day' | 'week' | 'month';

interface CalendarEvent {
  id: string;
  title: string;
  meta: string;
  day: number;
  startHour: number;
  durationHours: number;
  category: 'travel' | 'activity' | 'food' | 'lodging';
  location: string;
  cost?: string;
  notes?: string;
}

const monthDays = Array.from({ length: 31 }, (_, index) => index + 1);

const weekDays = [
  { label: 'MON', date: 12 },
  { label: 'TUE', date: 13 },
  { label: 'WED', date: 14 },
  { label: 'THU', date: 15 },
  { label: 'FRI', date: 16 },
  { label: 'SAT', date: 17 },
  { label: 'SUN', date: 18 },
];

const tripDays = [12, 13, 14, 15, 16, 17];
const weekendDays = [24, 25, 26];

const calendarEvents: CalendarEvent[] = [
  {
    id: 'flight-lisbon',
    title: 'Flight to Lisbon',
    meta: 'TP 236 · 2h',
    day: 12,
    startHour: 8.5,
    durationHours: 2.3,
    category: 'travel',
    location: 'Lisbon Airport',
    cost: '$180',
    notes: 'Arrive early for international check-in.',
  },
  {
    id: 'hotel-checkin',
    title: 'Hotel check-in',
    meta: 'Alfama Suites',
    day: 12,
    startHour: 14,
    durationHours: 1,
    category: 'lodging',
    location: 'Alfama Suites',
    cost: '$120',
    notes: 'Ask for luggage storage if arriving before check-in.',
  },
  {
    id: 'alfama',
    title: 'Alfama walking tour',
    meta: '2.5h · guided',
    day: 13,
    startHour: 10,
    durationHours: 2.5,
    category: 'activity',
    location: 'Alfama',
    cost: '$24',
    notes: 'Wear comfortable shoes for the hills.',
  },
  {
    id: 'market',
    title: 'Time Out Market',
    meta: 'Lunch',
    day: 13,
    startHour: 13.5,
    durationHours: 1.2,
    category: 'food',
    location: 'Time Out Market Lisbon',
    cost: '$28',
    notes: 'Good lunch stop after the walking tour.',
  },
  {
    id: 'sintra',
    title: 'Sintra day trip',
    meta: 'Pena Palace + Moorish Castle · 6h',
    day: 14,
    startHour: 9,
    durationHours: 6,
    category: 'activity',
    location: 'Sintra',
    cost: '$38',
    notes: 'Includes Pena Palace and Moorish Castle.',
  },
  {
    id: 'porto-train',
    title: 'Train to Porto',
    meta: 'Alfa Pendular · 3h',
    day: 15,
    startHour: 11,
    durationHours: 3,
    category: 'travel',
    location: 'Lisbon Santa Apolónia',
    cost: '$34',
    notes: 'Book Alfa Pendular seats in advance.',
  },
  {
    id: 'ribeira',
    title: 'Ribeira riverside',
    meta: 'Stroll',
    day: 15,
    startHour: 16,
    durationHours: 1.2,
    category: 'activity',
    location: 'Ribeira',
    notes: 'Golden hour riverside walk.',
  },
  {
    id: 'douro',
    title: 'Douro wine cellars',
    meta: 'Tasting · 2h',
    day: 16,
    startHour: 18,
    durationHours: 2,
    category: 'food',
    location: 'Vila Nova de Gaia',
    cost: '$42',
    notes: 'Reserve tasting slot before arrival.',
  },
  {
    id: 'flight-home',
    title: 'Flight home',
    meta: 'TP 441 · 2h',
    day: 17,
    startHour: 12,
    durationHours: 2,
    category: 'travel',
    location: 'Porto Airport',
    cost: '$170',
    notes: 'Leave Porto city center by 09:30.',
  },
];

const hours = Array.from({ length: 11 }, (_, index) => index + 7);

const CalendarPage: React.FC = () => {
  const [view, setView] = React.useState<CalendarView>('week');
  const [selectedDay, setSelectedDay] = React.useState(14);
  const [selectedEvent, setSelectedEvent] = React.useState<CalendarEvent | null>(calendarEvents[4]);

  const selectedDayEvents = calendarEvents.filter((event) => event.day === selectedDay);
  const rangeTitle = view === 'day' ? `Oct ${selectedDay}, 2026` : view === 'month' ? 'October 2026' : 'Oct 12 – 18, 2026';

  return (
    <DashboardLayout activeSection="calendar" wide>
      <div className="v2-calendar-page">
        <aside className="v2-calendar-page__sidebar">
          <button type="button" className="v2-calendar-page__add">
            + Add to calendar
          </button>

          <MiniCalendar selectedDay={selectedDay} onSelectDay={setSelectedDay} />

          <section className="v2-calendar-page__legend" aria-label="My trips legend">
            <h2>My trips</h2>
            <LegendItem color="forest" label="Portugal Escape" />
            <LegendItem color="blue" label="Amsterdam Weekend" />
            <LegendItem color="green" label="Activities" />
            <LegendItem color="gold" label="Food & dining" />
          </section>

          <EventDetails event={selectedEvent} onClose={() => setSelectedEvent(null)} />
        </aside>

        <main className="v2-calendar-page__main">
          <header className="v2-calendar-page__header">
            <button type="button" className="v2-calendar-page__today">Today</button>
            <div className="v2-calendar-page__range">
              <button type="button" aria-label="Previous week">‹</button>
              <button type="button" aria-label="Next week">›</button>
              <h1>{rangeTitle}</h1>
            </div>
            <div className="v2-calendar-page__view-toggle">
              {(['day', 'week', 'month'] as CalendarView[]).map((viewOption) => (
                <button
                  type="button"
                  key={viewOption}
                  className={view === viewOption ? 'v2-calendar-page__view-toggle--active' : undefined}
                  onClick={() => setView(viewOption)}
                >
                  {capitalize(viewOption)}
                </button>
              ))}
            </div>
          </header>

          {view === 'week' && (
            <WeekCalendar
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
              onSelectEvent={setSelectedEvent}
            />
          )}

          {view === 'day' && (
            <DayCalendar
              selectedDay={selectedDay}
              events={selectedDayEvents}
              onSelectEvent={setSelectedEvent}
            />
          )}

          {view === 'month' && (
            <MonthCalendar
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
              onSelectEvent={setSelectedEvent}
            />
          )}
        </main>
      </div>
    </DashboardLayout>
  );
};

interface MiniCalendarProps {
  selectedDay: number;
  onSelectDay: (day: number) => void;
}

const MiniCalendar: React.FC<MiniCalendarProps> = ({ selectedDay, onSelectDay }) => (
  <section className="v2-mini-calendar" aria-label="October 2026 mini calendar">
    <header>
      <h2>October 2026</h2>
      <div>
        <button type="button" aria-label="Previous month">‹</button>
        <button type="button" aria-label="Next month">›</button>
      </div>
    </header>
    <div className="v2-mini-calendar__weekdays">
      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
        <span key={`${day}-${index}`}>{day}</span>
      ))}
    </div>
    <div className="v2-mini-calendar__days">
      {[27, 28, 29, 30].map((day) => (
        <span key={`prev-${day}`} className="v2-mini-calendar__day--muted">{day}</span>
      ))}
      {monthDays.map((day) => {
        const isTripDay = tripDays.includes(day);
        const isWeekend = weekendDays.includes(day);
        const isSelected = day === selectedDay;
        return (
          <button
            type="button"
            key={day}
            onClick={() => onSelectDay(day)}
            className={[
              isTripDay ? 'v2-mini-calendar__day--trip' : '',
              isWeekend ? 'v2-mini-calendar__day--weekend' : '',
              isSelected ? 'v2-mini-calendar__day--selected' : '',
            ].filter(Boolean).join(' ')}
          >
            {day}
          </button>
        );
      })}
    </div>
  </section>
);

interface WeekCalendarProps {
  selectedDay: number;
  onSelectDay: (day: number) => void;
  onSelectEvent: (event: CalendarEvent) => void;
}

const WeekCalendar: React.FC<WeekCalendarProps> = ({ selectedDay, onSelectDay, onSelectEvent }) => (
  <section className="v2-calendar-week" aria-label="Week calendar">
    <div className="v2-calendar-week__days">
      <div />
      {weekDays.map((day) => (
        <button
          type="button"
          key={day.date}
          onClick={() => onSelectDay(day.date)}
          className={day.date === selectedDay ? 'v2-calendar-week__day--active' : undefined}
        >
          <span>{day.label}</span>
          <strong>{day.date}</strong>
        </button>
      ))}
    </div>

    <div className="v2-calendar-week__all-day">
      <span>All-day</span>
      <div className="v2-calendar-week__all-day-event">
        Portugal Escape · Lisbon → Sintra → Porto
      </div>
    </div>

    <div className="v2-calendar-week__grid">
      <div className="v2-calendar-week__times">
        {hours.map((hour) => (
          <span key={hour}>{formatHour(hour)}</span>
        ))}
      </div>
      <div className="v2-calendar-week__columns">
        {weekDays.map((day) => (
          <div
            key={day.date}
            className={`v2-calendar-week__column ${day.date === selectedDay ? 'v2-calendar-week__column--active' : ''}`}
          >
            {calendarEvents
              .filter((event) => event.day === day.date)
              .map((event) => (
                <CalendarEventBlock key={event.id} event={event} onSelect={onSelectEvent} />
              ))}
          </div>
        ))}
      </div>
    </div>
  </section>
);

interface DayCalendarProps {
  selectedDay: number;
  events: CalendarEvent[];
  onSelectEvent: (event: CalendarEvent) => void;
}

const DayCalendar: React.FC<DayCalendarProps> = ({ selectedDay, events, onSelectEvent }) => (
  <section className="v2-calendar-day" aria-label={`Day calendar for October ${selectedDay}`}>
    <header>
      <span>{weekDays.find((day) => day.date === selectedDay)?.label || 'DAY'}</span>
      <strong>{selectedDay}</strong>
      <p>{events.length} scheduled item{events.length === 1 ? '' : 's'}</p>
    </header>
    <div className="v2-calendar-day__timeline">
      {hours.map((hour) => (
        <div key={hour} className="v2-calendar-day__row">
          <time>{formatHour(hour)}</time>
          <div>
            {events
              .filter((event) => Math.floor(event.startHour) === hour)
              .map((event) => (
                <button
                  type="button"
                  key={event.id}
                  className={`v2-calendar-day__event v2-calendar-day__event--${event.category}`}
                  onClick={() => onSelectEvent(event)}
                >
                  <strong>{formatEventTime(event.startHour)} {event.title}</strong>
                  <span>{event.meta}</span>
                </button>
              ))}
          </div>
        </div>
      ))}
    </div>
  </section>
);

interface MonthCalendarProps {
  selectedDay: number;
  onSelectDay: (day: number) => void;
  onSelectEvent: (event: CalendarEvent) => void;
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({ selectedDay, onSelectDay, onSelectEvent }) => (
  <section className="v2-calendar-month" aria-label="Month calendar for October 2026">
    <div className="v2-calendar-month__weekdays">
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
        <span key={day}>{day}</span>
      ))}
    </div>
    <div className="v2-calendar-month__grid">
      {[27, 28, 29, 30].map((day) => (
        <div key={`prev-month-${day}`} className="v2-calendar-month__cell v2-calendar-month__cell--muted">
          <span>{day}</span>
        </div>
      ))}
      {monthDays.map((day) => {
        const events = calendarEvents.filter((event) => event.day === day);
        return (
          <button
            type="button"
            key={day}
            className={`v2-calendar-month__cell ${day === selectedDay ? 'v2-calendar-month__cell--selected' : ''}`}
            onClick={() => onSelectDay(day)}
          >
            <span>{day}</span>
            {events.slice(0, 2).map((event) => (
              <em
                key={event.id}
                className={`v2-calendar-month__event v2-calendar-month__event--${event.category}`}
                onClick={(eventClick) => {
                  eventClick.stopPropagation();
                  onSelectEvent(event);
                }}
              >
                {event.title}
              </em>
            ))}
            {events.length > 2 && <small>+{events.length - 2} more</small>}
          </button>
        );
      })}
    </div>
  </section>
);

interface LegendItemProps {
  color: 'forest' | 'blue' | 'green' | 'gold';
  label: string;
}

const LegendItem: React.FC<LegendItemProps> = ({ color, label }) => (
  <div className="v2-calendar-page__legend-item">
    <span className={`v2-calendar-page__legend-dot v2-calendar-page__legend-dot--${color}`} />
    <p>{label}</p>
  </div>
);

interface CalendarEventBlockProps {
  event: CalendarEvent;
  onSelect: (event: CalendarEvent) => void;
}

const CalendarEventBlock: React.FC<CalendarEventBlockProps> = ({ event, onSelect }) => {
  const top = ((event.startHour - 7) / 11) * 100;
  const height = (event.durationHours / 11) * 100;

  return (
    <button
      type="button"
      className={`v2-calendar-event v2-calendar-event--${event.category}`}
      onClick={() => onSelect(event)}
      style={{
        top: `${top}%`,
        height: `${height}%`,
      }}
    >
      <strong>{formatEventTime(event.startHour)} {event.title}</strong>
      <span>{event.meta}</span>
    </button>
  );
};

interface EventDetailsProps {
  event: CalendarEvent | null;
  onClose: () => void;
}

const EventDetails: React.FC<EventDetailsProps> = ({ event, onClose }) => {
  if (!event) {
    return (
      <section className="v2-calendar-event-details v2-calendar-event-details--empty">
        <h2>Event details</h2>
        <p>Select an itinerary block to see details and edit options.</p>
      </section>
    );
  }

  return (
    <section className="v2-calendar-event-details">
      <header>
        <div>
          <span className={`v2-calendar-event-details__category v2-calendar-event-details__category--${event.category}`}>
            {capitalize(event.category)}
          </span>
          <h2>{event.title}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close event details">×</button>
      </header>
      <dl>
        <div>
          <dt>When</dt>
          <dd>Oct {event.day}, {formatEventTime(event.startHour)} · {event.durationHours}h</dd>
        </div>
        <div>
          <dt>Where</dt>
          <dd>{event.location}</dd>
        </div>
        {event.cost && (
          <div>
            <dt>Cost</dt>
            <dd>{event.cost}</dd>
          </div>
        )}
        {event.notes && (
          <div>
            <dt>Notes</dt>
            <dd>{event.notes}</dd>
          </div>
        )}
      </dl>
      <div className="v2-calendar-event-details__actions">
        <button type="button">Edit time</button>
        <button type="button">Edit place</button>
        <button type="button">Remove</button>
      </div>
    </section>
  );
};

function formatHour(hour: number): string {
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function formatEventTime(hour: number): string {
  const wholeHour = Math.floor(hour);
  const minutes = Math.round((hour - wholeHour) * 60);
  return `${String(wholeHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default CalendarPage;
